import { visitorMediaUrl, visitorMediaFallbackUrl } from './visitorMedia';

/**
 * Replace placeholders in template with visitor data
 * @param {string} template - HTML template with placeholders
 * @param {Object} visitorData - Visitor data object
 * @param {string} qrCodeDataUrl - QR code data URL
 * @returns {string} HTML with placeholders replaced
 */
export const replacePlaceholders = (template, visitorData, qrCodeDataUrl = '') => {
  if (!template) {
    console.error('No template provided');
    return '';
  }

  if (!visitorData) {
    console.error('No visitor data provided');
    return template;
  }

  let processedTemplate = template;

  // Replace {{TokenNo}}
  const tokenNo = visitorData.token || visitorData.code || 'N/A';
  processedTemplate = processedTemplate.replace(/\{\{TokenNo\}\}/g, escapeHtml(tokenNo));

  // Replace {{NameofTheVisitor}}
  const visitorName = visitorData.vname || visitorData.name || 'N/A';
  processedTemplate = processedTemplate.replace(/\{\{NameofTheVisitor\}\}/g, escapeHtml(visitorName));

  // Replace {{MobileNo}}
  const mobileNo = visitorData.vphone || visitorData.mobile || 'N/A';
  processedTemplate = processedTemplate.replace(/\{\{MobileNo\}\}/g, escapeHtml(mobileNo));

  // Replace {{PhotoOfTheVisitor}} with img tag
  const photoHtml = generatePhotoHtml(visitorData);
  processedTemplate = processedTemplate.replace(/\{\{PhotoOfTheVisitor\}\}/g, photoHtml);

  // Replace {{QRCode}} with QR code img tag
  const qrCodeHtml = generateQRCodeHtml(qrCodeDataUrl);
  processedTemplate = processedTemplate.replace(/\{\{QRCode\}\}/g, qrCodeHtml);

  return processedTemplate;
};

/**
 * Generate photo HTML img tag
 * @param {Object} visitorData - Visitor data object
 * @returns {string} HTML img tag or placeholder
 */
const generatePhotoHtml = (visitorData) => {
  const embedded = visitorData.photoPrintSrc;
  if (embedded) {
    return `<img src="${embedded}" alt="Visitor Photo" style="width: 80px; height: 100px; object-fit: cover; border: 2px solid #ddd; border-radius: 4px;" />`;
  }

  const photo = visitorData.profile_pic || visitorData.photo || visitorData.profile_image;
  const photoUrl = visitorMediaUrl('profile_image', photo);
  if (!photoUrl) {
    return '<img src="https://via.placeholder.com/80x100?text=No+Photo" alt="Visitor Photo" style="width: 80px; height: 100px; object-fit: cover; border: 2px solid #ddd; border-radius: 4px;" />';
  }

  const fallbackUrl = visitorMediaFallbackUrl('profile_image', photo);
  const onError = fallbackUrl
    ? `this.onerror=null;this.src='${escapeHtml(fallbackUrl)}';`
    : `this.src='https://via.placeholder.com/80x100?text=No+Photo';`;

  return `<img src="${escapeHtml(photoUrl)}" alt="Visitor Photo" style="width: 80px; height: 100px; object-fit: cover; border: 2px solid #ddd; border-radius: 4px;" onerror="${onError}" />`;
};

/**
 * Generate QR code HTML img tag
 * @param {string} qrCodeDataUrl - QR code data URL
 * @returns {string} HTML img tag or placeholder
 */
const generateQRCodeHtml = (qrCodeDataUrl) => {
  if (!qrCodeDataUrl) {
    return '<div style="width: 80px; height: 80px; background: #f0f0f0; display: flex; align-items: center; justify-content: center; font-size: 10px; color: #999;">QR Code</div>';
  }

  return `<img src="${qrCodeDataUrl}" alt="QR Code" style="width: 80px; height: 80px;" />`;
};

/**
 * Escape HTML to prevent XSS
 * @param {string} text - Text to escape
 * @returns {string} Escaped text
 */
const escapeHtml = (text) => {
  if (typeof text !== 'string') {
    return String(text);
  }

  const map = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;'
  };

  return text.replace(/[&<>"']/g, (char) => map[char]);
};

/**
 * Generate complete badge HTML for a single visitor
 * @param {string} template - HTML template
 * @param {Object} visitorData - Visitor data
 * @param {string} qrCodeDataUrl - QR code data URL
 * @returns {string} Complete HTML badge
 */
export const generateBadgeHtml = async (template, visitorData, qrCodeDataUrl) => {
  return replacePlaceholders(template, visitorData, qrCodeDataUrl);
};

/**
 * Generate HTML for multiple badges (for bulk printing)
 * @param {string} template - HTML template
 * @param {Array<Object>} visitorsData - Array of visitor data objects
 * @param {Object} qrCodesMap - Map of token numbers to QR code data URLs
 * @returns {string} Combined HTML for all badges with page breaks
 */
export const generateBulkBadgesHtml = (template, visitorsData, qrCodesMap = {}) => {
  if (!template || !visitorsData || visitorsData.length === 0) {
    return '';
  }

  // Extract the style and body content from template
  const styleMatch = template.match(/<style[^>]*>([\s\S]*?)<\/style>/i);
  const styles = styleMatch ? styleMatch[0] : '';

  // Generate individual badges
  const badges = visitorsData.map((visitor, index) => {
    const tokenNo = visitor.token || visitor.code || '';
    const qrCode = qrCodesMap[tokenNo] || '';
    
    let badgeHtml = replacePlaceholders(template, visitor, qrCode);
    
    // Remove <html>, <head>, <body> tags for individual badges (keep only content)
    badgeHtml = badgeHtml.replace(/<\/?html[^>]*>/gi, '');
    badgeHtml = badgeHtml.replace(/<\/?head[^>]*>/gi, '');
    badgeHtml = badgeHtml.replace(/<\/?body[^>]*>/gi, '');
    badgeHtml = badgeHtml.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, ''); // Remove inline styles
    
    return badgeHtml.trim();
  }).join('\n');

  // Combine into single HTML document
  return `<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>Visitor Badges - Bulk Print</title>
    ${styles}
</head>
<body>
${badges}
</body>
</html>`;
};

const templateReplacerUtils = {
  replacePlaceholders,
  generateBadgeHtml,
  generateBulkBadgesHtml
};

export default templateReplacerUtils;
